import { useMemo, useState } from "react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  format,
  addMonths,
  subMonths,
} from "date-fns";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { SocialPost } from "@shared/schema";

interface CalendarViewProps {
  posts: SocialPost[];
  onSelectPost: (post: SocialPost) => void;
}

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  needs_approval: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200",
  scheduled: "bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-200",
  published: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200",
  failed: "bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-200",
};

export function CalendarView({ posts, onSelectPost }: CalendarViewProps) {
  const [cursor, setCursor] = useState(() => new Date());

  const days = useMemo(() => {
    const monthStart = startOfMonth(cursor);
    const monthEnd = endOfMonth(cursor);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [cursor]);

  const postsByDay = useMemo(() => {
    const map = new Map<string, SocialPost[]>();
    for (const p of posts) {
      if (!p.scheduledAt) continue;
      const key = format(new Date(p.scheduledAt), "yyyy-MM-dd");
      const list = map.get(key) || [];
      list.push(p);
      map.set(key, list);
    }
    return map;
  }, [posts]);

  return (
    <Card data-testid="card-calendar-view">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
        <div className="text-lg font-semibold" data-testid="text-calendar-month">
          {format(cursor, "MMMM yyyy")}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCursor((c) => subMonths(c, 1))}
            data-testid="button-calendar-prev"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCursor(new Date())}
            data-testid="button-calendar-today"
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCursor((c) => addMonths(c, 1))}
            data-testid="button-calendar-next"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-7 text-xs uppercase tracking-wide text-muted-foreground border-b">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="py-2 text-center">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 grid-rows-6 border-l border-t">
          {days.map((day) => {
            const key = format(day, "yyyy-MM-dd");
            const dayPosts = postsByDay.get(key) || [];
            const inMonth = isSameMonth(day, cursor);
            const isToday = isSameDay(day, new Date());
            return (
              <div
                key={key}
                className={[
                  "border-r border-b min-h-24 p-1.5 text-xs",
                  inMonth ? "bg-background" : "bg-muted/30 text-muted-foreground",
                  isToday ? "ring-1 ring-primary ring-inset" : "",
                ].join(" ")}
                data-testid={`cell-day-${key}`}
              >
                <div className="font-medium mb-1">{format(day, "d")}</div>
                <div className="space-y-1">
                  {dayPosts.slice(0, 3).map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => onSelectPost(p)}
                      className={[
                        "w-full text-left rounded px-1.5 py-0.5 truncate hover:opacity-80 transition",
                        STATUS_COLORS[p.status] || "bg-muted",
                      ].join(" ")}
                      title={p.campaignTag || p.internalNote?.slice(0, 80) || "Untitled"}
                      data-testid={`calendar-post-${p.id}`}
                    >
                      {p.campaignTag || p.internalNote?.slice(0, 30) || "Untitled"}
                    </button>
                  ))}
                  {dayPosts.length > 3 && (
                    <Badge variant="outline" className="text-[10px] px-1 py-0">
                      +{dayPosts.length - 3} more
                    </Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
